<template>
    <div class="booking-form-new form-wrapper">
        <div class="booking-container">
            <el-form @submit.native.prevent :model="bookForm" :rules="rules" ref="bookForm" autocomplete="" class="card">

                <!-- Booking Form -->
                <el-row v-if="!done" :gutter="40" type="flex" justify="space-between">

                    <!-- Customer and service data -->
                    <el-col :xs="24" :span="16">

                        <!-- Contact Information -->
                        <div class="section section-separator">

                            <div class="section-header">
                                <h2>{{$t('contact_information')}}</h2>
                                <p>{{$t('contact_information_description')}}</p>
                            </div>

                            <div class="section-body">
                                <el-row :gutter="16">

                                    <el-col :xs="24" :sm="12">
                                        <el-form-item prop="first_name" :label="$t('placeholder_first_name')">
                                            <el-input :placeholder="$t('new_placeholder_first_name')" v-model="bookForm.first_name" required />
                                        </el-form-item>
                                    </el-col>

                                    <el-col :xs="24" :sm="12">
                                        <el-form-item prop="last_name" :label="$t('placeholder_last_name')">
                                            <el-input :placeholder="$t('new_placeholder_last_name')" v-model="bookForm.last_name" />
                                        </el-form-item>
                                    </el-col>

                                </el-row>

                                <el-row :gutter="16">

                                    <el-col :xs="24" :sm="12">
                                        <el-form-item prop="email" :label="$t('placeholder_email')">
                                            <el-input :placeholder="$t('new_placeholder_email')" v-model="bookForm.email" required />
                                        </el-form-item>
                                    </el-col>

                                    <el-col :xs="24" :sm="12">
                                        <el-form-item prop="phone" :label="$t('placeholder_phone')">
                                            <div @keydown="isNumber">
                                                <el-input :placeholder="$t('new_placeholder_phone')" v-model="bookForm.phone" required>
                                                    <template slot="prepend">{{ serviceLocale.phone_prefix }}</template>
                                                </el-input>
                                            </div>
                                        </el-form-item>
                                    </el-col>

                                </el-row>

                                <el-row>
                                    <el-form-item>
                                        <el-checkbox v-if="clientId == 3151" v-model="checked">
                                            I'm giving my consent to receive SMS notifications
                                        </el-checkbox>
                                        <el-checkbox v-else v-model="checked">
                                            {{$t('notifications_consent')}}
                                        </el-checkbox>
                                    </el-form-item>
                                </el-row>
                            </div>

                            <Divider :primary-color="customizer.appearance.accentColor" />
                        </div>

                        <!-- Service Address -->
                        <div class="section section-separator" >

                            <div class="section-header">
                                <h2>{{$t('service_address')}}</h2>
                                <p>{{$t('service_address_description')}}</p>
                            </div>

                            <el-row :gutter="16">

                                <el-col :xs="16" :sm="18">
                                    <el-form-item prop="street_address" :label="$t('placeholder_street')">
                                        <gmap-autocomplete
                                            :value="bookForm.street_address"
                                            :placeholder="$t('new_placeholder_street')"
                                            @place_changed="setPlace"
                                            @input="setPlace"
                                            :select-first-on-enter="true"
                                            style="width: 100%;"
                                            class="gmap-autocomplete"
                                        >
                                        </gmap-autocomplete>
                                        <el-input :placeholder="$t('new_placeholder_street')" v-model="bookForm.street_address" style="display: none;" />
                                    </el-form-item>
                                </el-col>

                                <el-col :xs="8" :sm="6">
                                    <el-form-item prop="apt_number" :label="$t('placeholder_apt')">
                                        <el-input :placeholder="$t('new_placeholder_apt')" v-model="bookForm.apt_number" />
                                    </el-form-item>
                                </el-col>

                            </el-row>

                            <el-row :gutter="16">

                                <el-col :xs="serviceLocale.states ? 24 : 16" :sm="serviceLocale.states ? 9 : 18">
                                    <el-form-item prop="city" :label="$t('placeholder_city')">
                                        <el-input :placeholder="$t('new_placeholder_city')" v-model="bookForm.city" />
                                    </el-form-item>
                                </el-col>

                                <template v-if="serviceLocale.states">
                                    <el-col :xs="16" :sm="9">
                                        <el-form-item prop="selected_state" :label="$t('placeholder_state')">
                                            <el-select v-model="bookForm.selected_state" autocomplete="disabled" filterable :placeholder="$t('new_placeholder_state')" style="width: 100%;">
                                                <el-option
                                                    v-for="state in serviceLocale.states"
                                                    :key="state.abbreviation"
                                                    :label="state.name"
                                                    :value="state.abbreviation">
                                                </el-option>
                                            </el-select>
                                        </el-form-item>
                                    </el-col>
                                </template>

                                <el-col :xs="8" :sm="6">
                                    <el-form-item prop="zipcode" :label=" _placeholder('zipcode') || $t('placeholder_zip')">
                                        <el-input :placeholder="$t('new_placeholder_zip')" v-model="bookForm.zipcode" />
                                    </el-form-item>
                                </el-col>

                            </el-row>

                            <Divider :primary-color="customizer.appearance.accentColor" />
                        </div>

                        <!-- Service Category -->
                        <div class="section section-separator" v-if="selected_category">

                            <div class="section-header">
                                <h2>{{$t('service_category')}}</h2>
                                <p>{{$t('service_category_description')}}</p>
                            </div>

                            <el-form-item prop="category" label="Category">
                                <el-select v-model="bookForm.category_id" value-key="id" class="el-form-item service-column">
                                    <el-option
                                        v-for="(category) in bookForm.categories"
                                        :key="category.id"
                                        :label="category.name"
                                        :value="category.id"
                                    >
                                        {{ category.name }}
                                    </el-option>
                                </el-select>
                            </el-form-item>

                            <Divider :primary-color="customizer.appearance.accentColor" />
                        </div>

                        <!-- Services & Pricing Parameters -->
                        <div class="section section-separator" v-if="selected_service">

                            <div class="section-header">
                                <h2>{{$t('services_title')}}</h2>
                                <p>{{$t('services_description')}}</p>
                            </div>


                            <!-- Services with new style (images) -->
                            <div v-if="customizer.appearance.servicesStyle === 'default_new'" class="section section-separator" >

                                    <div class="section-body services-images-section">
                                        <Service
                                            v-for="(service, index) in bookForm.services"
                                            :key="service.id"
                                            @click="selected_service = service"
                                            :service="service"
                                            :is-active="service === selected_service"
                                            :currency="currencySymbol"

                                        />
                                    </div>

                                <template v-if="bookForm.pricings.length || selected_service.is_hourly">
                                    <Divider :primary-color="customizer.appearance.accentColor" />

                                    <div class="section-header">
                                        <h2>Service Modifiers</h2>
                                        <p>Customize your service </p>
                                    </div>
                                </template>

                            </div>


                            <div class="section-body services-section">

                                <!-- Service input select -->
                                <el-form-item v-if="customizer.appearance.servicesStyle === 'default' || !(customizer?.appearance?.servicesStyle) " label="Service">
                                    <el-select v-model="selected_service" value-key="id">
                                        <el-option
                                            v-for="(service) in bookForm.services"
                                            :key="service.id"
                                            :label="service.name"
                                            :value="service"
                                            v-if="service.is_active"
                                        >
                                            {{ service.name }}
                                        </el-option>
                                    </el-select>
                                </el-form-item>


                                <!-- Pricing parameters input select -->
                                <el-form-item label="Pricing Parameter" v-for="n in selected_service.groups" :key="n" v-if="bookForm.pricings">
                                    <el-select v-model="selected_pricing[n]" value-key="id">
                                        <el-option
                                            v-for="price in bookForm.pricings"
                                            :key="price.id"
                                            :label="price.name"
                                            :value="price"
                                            v-if="n == price.group"
                                        >
                                                {{ price.name }}
                                        </el-option>
                                    </el-select>
                                </el-form-item>

                                <el-form-item label="Workers" v-if="selected_service.is_hourly">
                                    <el-select v-model="selected_workers" value-key="id">
                                        <el-option
                                            v-for="workers in selected_service.cleaners"
                                            :key="workers"
                                            :label="label_workers(workers)"
                                            :value="workers">
                                                {{ label_workers(workers) }}
                                        </el-option>
                                    </el-select>
                                </el-form-item>

                                <el-form-item label="Hours" v-if="selected_service.is_hourly">
                                    <el-select v-model="selected_workers_time" value-key="id">
                                        <el-option
                                            v-for="time in selected_service.hours"
                                            :key="time"
                                            :label="label_time(time)"
                                            :value="time" >
                                                {{ label_time(time) }}
                                        </el-option>
                                    </el-select>
                                </el-form-item>

                            </div>

                            <Divider :primary-color="customizer.appearance.accentColor" />
                        </div>

                        <!-- Extras -->
                        <div class="section section-separator" v-if="bookForm.extras">

                            <div class="section-header">
                                <h2>{{$t('extras_title')}}</h2>
                                <p>{{$t('extras_description')}}</p>
                            </div>

                            <el-form-item>
                                <div class="section-body extras-section">
                                    <Extra
                                        v-for="(item, index) in bookForm.extras"
                                        :key="index"
                                        @click="toggleExtra($event,item.extra)"
                                        :extra="item.extra"
                                        :is-active="isAdded(item.id_extra)"
                                        :currency="currencySymbol"
                                    />
                                </div>
                            </el-form-item>

                            <Divider :primary-color="customizer.appearance.accentColor" />
                        </div>

                        <!-- Time -->
                        <div class="section section-separator">

                            <div class="section-header" id="pickAday">

                                <!-- Dewey Lawns custom wording -->
                                <template v-if="clientId == 3127">
                                    <h2 style="padding-bottom: 6px;">{{$t('when_to_come_dewey_lawns')}}</h2>
                                    <p style="text-align: left; padding-bottom: 12px;">{{$t('when_to_come_dewey_lawns_description_1')}}</p>
                                    <p style="text-align: left; padding-bottom: 12px;">{{$t('when_to_come_dewey_lawns_description_2')}}</p>
                                    <p style="text-align: left; padding-bottom: 12px;">{{$t('when_to_come_dewey_lawns_description_3')}}</p>
                                    <p style="text-align: left; padding-bottom: 12px;">{{$t('when_to_come_dewey_lawns_description_4')}}</p>
                                </template>

                                <!-- Regular wording -->
                                <template v-else>
                                    <h2>{{$t('when_to_come')}}</h2>
                                    <p>{{$t('when_to_come_description')}}</p>
                                </template>
                            </div>

                            <el-row :gutter="16" id="dateColumns">
                                <el-col :sm="24" :md="12">
                                    <el-form-item prop="selected_date">
                                        <el-date-picker
                                            v-model="bookForm.selected_date"
                                            type="date"
                                            :placeholder="$t('new_placeholder_day')"
                                            :picker-options="pickerOptions"
                                            format="MM-dd-yyyy"
                                            value-format="yyyy-MM-dd"
                                            style="width: 100%;"
                                        />
                                    </el-form-item>
                                </el-col>
                                <el-col :sm="24" :md="12">
                                    <el-form-item prop="selected_hour">
                                        <el-select
                                            v-model="bookForm.selected_hour"
                                            no-data-text="No Availability"
                                            :placeholder="arrivalLabel"
                                            style="width: 100%;"
                                            class="el-time-editor"
                                        >
                                            <template slot="prefix">
                                                <i class="el-icon-time"></i>
                                            </template>

                                            <el-option
                                                v-for="hour in currentDay"
                                                :key="hour"
                                                :label="hourLabel(hour)"
                                                :value="hour"
                                                v-if="hourLabel(hour)"
                                            >
                                                <span>{{ hourLabel(hour) }}</span>
                                            </el-option>
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                            </el-row>

                            <Divider :primary-color="customizer.appearance.accentColor" />
                        </div>

                        <!-- Frequencies -->
                        <div class="section section-separator">

                            <div class="section-header">
                                <h2>{{$t('how_often')}}</h2>
                                <p>
                                    <span>{{$t('how_often_description')}}</span>
                                    <!-- <span v-if="settings.zero_discount_to_first_booking == 1" class="frequency-discount">{{$t('how_often_discount')}}</span> -->
                                </p>
                            </div>
                            <el-form-item>
                                <div class="section-body frequencies-section">

                                    <frequency
                                        :key="index"
                                        v-for="(item, index) in selected_service?.service_frequency"
                                        @click="bookForm.frequency_id = item.frequency.id"
                                        :frequency="item.frequency"
                                        :is-active="frequency_selected === item.frequency"
                                    />
                                </div>
                            </el-form-item>

                            <Divider :primary-color="customizer.appearance.accentColor" />
                        </div>

                        <!-- Additional Information -->
                        <div class="section section-separator">
                            <div class="section-header">
                                <h2>{{$t('notes')}}</h2>
                                <p>{{$t('notes_description')}}</p>
                            </div>

                            <!-- Custom Fields -->
                            <div v-if="customFields.length > 0" class="section section-separator">

                                <div v-for="(field, index) in customFields" :key="index" class="custom-field-item">
                                    <!-- Text Input -->
                                    <el-form-item v-if="field.type === 'text'" :label="field.label" :prop="'custom_field_' + index" :rules="field.required ? {
                                        required: !customFieldResponses[index], message: 'Field Required', trigger: ['blur', 'change']
                                    } : []">
                                        <el-input 
                                            v-model="customFieldResponses[index]"
                                            :placeholder="field.placeholder || field.label"
                                        />
                                    </el-form-item>

                                    <!-- Select Dropdown -->
                                    <el-form-item v-if="field.type === 'select'" :label="field.label" :prop="'custom_field_' + index" :rules="field.required ? {
                                        required: !customFieldResponses[index], message: 'Field Required', trigger: ['blur', 'change']
                                    } : []">
                                        <el-radio-group 
                                            v-model="customFieldResponses[index]"
                                            class="custom-field-radio-group"
                                            :style="field.options.length > 2 ? 'grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));' : ''"
                                        >
                                            <el-radio
                                                v-for="option in field.options"
                                                :key="option"
                                                :label="option"
                                                class="custom-field-radio"
                                            >
                                                {{ option }}
                                            </el-radio>
                                        </el-radio-group>
                                    </el-form-item>

                                    <!-- Checkbox -->
                                    <!-- <el-form-item v-if="field.type === 'checkbox'" :prop="'custom_field_' + index" :label="field.label" :rules="field.required ? {
                                        required: !customFieldResponses[index], message: 'Field Required', trigger: ['blur', 'change']
                                    } : []">
                                        <el-checkbox v-model="customFieldResponses[index]" class="custom-field-checkbox" :style="!field.label ? 'margin-top: 12px;' : ''">
                                            {{ field.placeholder }}
                                        </el-checkbox>
                                    </el-form-item> -->
                                </div>
                            </div>

                            <!-- Notes -->
                            <el-form-item label="Anything else we should know?">
                                <el-input
                                    type="textarea"
                                    :rows="2"
                                    :placeholder="$t('new_placeholder_notes')"
                                    v-model="bookForm.additionalInformation">
                                </el-input>
                            </el-form-item>

                        </div>

                        <!-- Credit Card details -->
                        <div v-if="!customizer.skip_payment_requirement" class="section section-separator">
                            <Divider :primary-color="customizer.appearance.accentColor" />

                            <div class="section-header">
                                <h2>{{$t('cc_details')}}</h2>
                                <p>{{$t('cc_description')}}</p>
                            </div>

                            <!-- Error -->
                            <el-alert
                                v-if="error"
                                class="error-alert"
                                :title="$t('something_wrong')"
                                :description="$t('complete_request')"
                                type="error"
                                style="margin-bottom: 16px;"
                                show-icon
                            />

                            <!-- Select Payment Method -->
                            <div v-if="existPaymentMethods">
                                <PaymentMethods
                                    :stripe-key="stripeKey"
                                    :bookUser="{id:customer.id}"
                                    :guard="$guardModel"
                                    ref="PaymentMethods"
                                    :show-card-branding="false"
                                />
                            </div>

                            <!-- Add Payment Method -->
                            <div v-if="!existPaymentMethods">
                                <el-form-item v-if="isStripeLoaded" class="credit-card-container">
                                    <Card
                                        v-if="stripeKey"
                                        class='stripe-card'
                                        :class='{ complete }'
                                        :stripe='stripeKey'
                                        :options='stripeOptions'
                                        @change='complete = $event.complete'
                                    />
                                </el-form-item>
                                <el-alert v-else title="Warning: Please connect your Stripe account" type="warning" style="margin-bottom: 16px;"/>
                            </div>

                        </div>

                        <!-- Responsive Cleaning Plan -->
                        <div class="sidebar responsive">
                            <div class="cleaning-plan responsive" :style="{ top: priceBoxTop+'px', transition: 'all 0.4s ease 0.02s' }">

                                <!-- Discount Code -->
                                <div class="promo-code" v-if="!selected_discount && !selected_giftcard" style="border: none;">

                                    <el-form-item label="Do you have a Promo Code?">
                                        <el-row :gutter="8">

                                            <el-col :span="16">
                                                <el-input :placeholder="$t('new_placeholder_promo')" :disabled="promoExist()"  v-model="promo_code"></el-input>
                                            </el-col>

                                            <!-- Apply -->
                                            <el-col :span="8">
                                                <el-button type="primary" :class = "{'blue-bg' : promo_code != '' }" @click="promoCode(1)" :disabled="spinner_code || !promo_code">
                                                    <div class="spinner_code" :class="{'spinner': spinner_code}">
                                                        <div class="bounce1"></div>
                                                        <div class="bounce2"></div>
                                                        <div class="bounce3"></div>
                                                    </div>
                                                    <div v-if="!spinner_code">
                                                        {{ discount_text }}
                                                    </div>
                                                </el-button>
                                            </el-col>

                                        </el-row>
                                </el-form-item>

                                </div>

                                <h2 style="text-align: left;">{{$t('cleaning_plans')}}</h2>

                                <!-- Date & Hour -->
                                <div v-if="bookForm.selected_date && bookForm.selected_hour" class="date-hour">
                                    <span>{{ $moment(bookForm.selected_date).format('ll') }} @ {{ $moment(bookForm.selected_date + ' ' + bookForm.selected_hour).format('hh:mm a') }}</span>
                                </div>

                                <!-- Category -->
                                <div v-if="selected_category" class="category">
                                    <span>{{ selected_category.name }}</span>
                                </div>

                                <!-- Service -->
                                <div v-if="selected_pricing && selected_service" class="service">
                                    <span>{{ selected_service.name }}<template v-for="(pricing_parameter) in selected_pricing" v-if="pricing_parameter">, {{ pricing_parameter.name }}</template>
                                    </span>
                                </div>

                                <!-- Responsive Summary -->
                                <div class="summary">

                                    <!-- Per Cleaning -->
                                    <div class="pricing-item">
                                        <span class="label">{{$t('per_cleaning')}}</span>
                                        <span v-if="cleaningPrice">{{ currencySymbol }}{{ perCleaning() }}</span>
                                    </div>

                                    <!-- Extras -->
                                    <div v-if="bookForm.selected_extras_names.length > 0" >
                                        <div class="pricing-item">
                                            <span class="label">{{$t('selected_extras')}}</span>
                                            <span>{{ currencySymbol }}{{ bookForm.total_extra.toFixed(2) }}</span>
                                        </div>
                                        <div v-for="item in bookForm.selected_extras_names" :key="item.id" class="pricing-item selected-extras">
                                            <div>
                                                <span class="label">{{item.name}}</span>
                                                <span v-if="item.quantity_based" class="quantity-extra"> (x{{ item.quantity }})</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Frequency -->
                                    <div v-if="frequency_selected && TotalFrequencyDiscount > 0">
                                        <div v-if="Number(TotalFrequencyDiscount) >= 0" class="pricing-item">
                                            <span class="label">{{$t('frequency_discount')}}</span>
                                            <span style="color: var(--color-primary);">-{{ currencySymbol }}{{ Number(TotalFrequencyDiscount).toFixed(2) }}</span>
                                        </div>
                                        <div v-else>
                                            <span >Frequency Fee</span>
                                            <span style="color: var(--color-primary);">{{ currencySymbol }}{{ Number(TotalFrequencyDiscount).toFixed(2) | absolute }}</span>
                                        </div>
                                    </div>

                                    <!-- Gift Card -->
                                    <div v-if="gift_card_amount > 0">
                                        <div class="pricing-item">
                                            <span class="label">{{$t('gift_card')}}</span>
                                            <span style="color: var(--color-primary);">-{{ currencySymbol }}{{ gift_card_amount.toFixed(2) }}</span>
                                        </div>
                                        <div class="pricing-item selected-giftcard">
                                            <span class="label">{{ selected_giftcard.code }}</span>
                                            <div class="remove-discount" @click="removePromoCode()">
                                                <img src="/images/booking-form/trash.svg" alt="Remove Discount">
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Discount Code -->
                                    <div v-if="selected_discount">
                                        <div class="pricing-item">
                                            <span class="label">{{$t('discount_code')}}</span>
                                            <!-- Amount -->
                                            <span style="color: var(--color-primary);">-{{ currencySymbol }}{{ Number(TotalPromoDiscount).toFixed(2) | absolute }}</span>
                                        </div>
                                        <div class="pricing-item" style="margin-top: -5px;">
                                            <div class="center-discount">
                                                <span class="selected-discount">{{ selected_discount.code }}</span>

                                                <span class="discount-rate" v-if="selected_discount.percent">-{{ selected_discount.percent }}%</span>
                                                <div class="remove-discount" @click="removePromoCode()">
                                                    <img src="/images/booking-form/trash.svg" alt="Remove Discount">
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Same Day Booking Fee -->
                                    <div v-if="additionalFee && same_day_upcharge">
                                        <div class="pricing-item">
                                            <span class="label">{{ settings.fee_day_label }}</span>
                                            <span>{{ currencySymbol }}{{ same_day_upcharge.toFixed(2) }}</span>
                                        </div>
                                    </div>

                                    <!-- Sales Tax -->
                                    <div v-if="salesTax && salesTax.tax_enabled === 1">
                                        <div class="pricing-item">
                                            <span class="label">{{ salesTax.tax_label }}</span>
                                            <span>{{ currencySymbol }}{{ taxAmount.toFixed(2) }}</span>
                                        </div>
                                    </div>

                                </div>

                                <!-- Referral Program -->
                                <div v-if="customer && settings.referral_program_enabled" class="referrals">

                                    <div class="pricing-item">
                                        <span class="balance-label">
                                            {{$t('referral_balance')}}
                                        </span>
                                        <span class="balance-total">{{ currencySymbol }}{{ customer.referrals_balance.toFixed(2)}}</span>
                                    </div>

                                    <div class="pricing-item">
                                        <span class="apply">
                                            <el-checkbox style="color:#606266 !important; align-items: center;" v-model="referral_use_balance">{{$t('apply_balance')}}</el-checkbox>
                                        </span>
                                        <el-input-number controls-position="right" size="small" v-model="referral_amount_to_use" :min="0" :max="referral_max_amount_to_use" :precision="2" :style="{ color: referral_use_balance ? 'var(--color-primary)': '' }">
                                        </el-input-number>
                                    </div>

                                </div>

                                <!-- Total -->
                                <div v-if="!customizer.skip_payment_requirement" class="total" style="margin-bottom: 24px;">
                                    <div class="total-label">Total</div>
                                    <div v-if="totalPrice>=0" class="total-amount">{{ currencySymbol }}{{ totalPrice.toFixed(2) }}</div>
                                </div>

                            </div>
                        </div>

                        <!-- Submit Button -->
                        <el-form-item>
                            <el-button
                                @click="book"
                                :disabled="!stripeKey || !checked || spinner_actived"
                                :class="{'btn-disabled': !stripeKey || !checked || spinner_actived}"
                                class="complete-booking-button"
                                type="primary"
                                v-loading="spinner_actived"
                            >
                                <span v-if="!spinner_actived && stripeKey">{{$t('submit_button')}}</span>
                                <span v-if="!stripeKey">{{$t('stripe_keys')}}</span>
                            </el-button>
                                </el-form-item>

                        <c-card-brands v-if="!customizer.skip_payment_requirement" class="card-brands"></c-card-brands>

                    </el-col>

                    <!-- Column Divider -->
                    <el-col :span="0" style="position:relative; display:flex" class="column-divider">
                        <div class="sectionSeparator divider"></div>
                    </el-col>

                    <!-- Sidebar -->
                    <el-col :span="8" class="sidebar">

                        <!-- Questions & Answers -->
                        <div v-if="faq && (faq.length > 0)">

                            <div class="section-header">
                                <h2 style="text-align: left">{{$t('questions')}}</h2>
                            </div>

                            <el-form-item>
                                <el-tree
                                    :data="faqList"
                                    :props="{children: 'children', label: 'label'}"
                                    accordion
                                />
                            </el-form-item>

                        </div>

                        <!-- Feature Image -->
                        <div id="imageboxDiv"  class="image-container">
                            <img :src="vidThumb">
                        </div>

                        <!-- Discount Code -->
                        <div class="promo-code" v-if="!selected_discount && !selected_giftcard">

                            <el-row :gutter="8">

                                <el-col :span="14">
                                    <el-input :placeholder="$t('new_placeholder_promo')" :disabled="promoExist()"  v-model="promo_code"></el-input>
                                </el-col>

                                <!-- Apply -->
                                <el-col :span="10">
                                    <el-form-item>
                                        <el-button type="primary" :class = "{'blue-bg' : promo_code != '' }" @click="promoCode(1)" :disabled="spinner_code || !promo_code">
                                            <div class="spinner_code" :class="{'spinner': spinner_code}">
                                                <div class="bounce1"></div>
                                                <div class="bounce2"></div>
                                                <div class="bounce3"></div>
                                            </div>
                                            <div v-if="!spinner_code">
                                                {{ discount_text }}
                                            </div>
                                        </el-button>
                                    </el-form-item>
                                </el-col>

                            </el-row>

                        </div>

                        <!-- Cleaning Plan (Sidebar Sticky Section) -->
                        <div class="cleaning-plan" :style="{ top: priceBoxTop+'px', transition: 'all 0.4s ease 0.02s' }">

                            <h2 style="text-align: left">{{$t('cleaning_plans')}}</h2>

                            <!-- Date & Hour -->
                            <div v-if="bookForm.selected_date && bookForm.selected_hour" class="date-hour">
                                <span>{{ $moment(bookForm.selected_date).format('ll') }} @ {{ $moment(bookForm.selected_date + ' ' + bookForm.selected_hour).format('hh:mm a') }}</span>
                            </div>

                            <!-- Category -->
                            <div v-if="selected_category" class="category">
                                <span>{{ selected_category.name }}</span>
                            </div>

                            <!-- Service -->
                            <div v-if="selected_pricing && selected_service" class="service">
                                <span>{{ selected_service.name }}<template v-for="(pricing_parameter) in selected_pricing" v-if="pricing_parameter">, {{ pricing_parameter.name }}</template>
                                </span>
                            </div>

                            <!-- Summary -->
                            <div class="summary">

                                <!-- Per Cleaning -->
                                <div class="pricing-item">
                                    <span class="label">{{$t('per_cleaning')}}</span>
                                    <span v-if="cleaningPrice">{{ currencySymbol }}{{ perCleaning() }}</span>
                                </div>

                                <!-- Extras -->
                                <div v-if="bookForm.selected_extras_names.length > 0" >
                                    <div class="pricing-item">
                                        <span class="label">{{$t('selected_extras')}}</span>
                                        <span>{{ currencySymbol }}{{ bookForm.total_extra.toFixed(2) }}</span>
                                    </div>
                                    <div v-for="item in bookForm.selected_extras_names" :key="item.id" class="pricing-item selected-extras">
                                        <div>
                                            <span v-if="item.quantity_based" class="quantity-extra">{{ item.quantity }}X </span>
                                            <span class="label">{{item.name}}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Frequency -->
                                <div v-if="frequency_selected && TotalFrequencyDiscount > 0">
                                    <div v-if="Number(TotalFrequencyDiscount) >= 0" class="pricing-item">
                                        <span class="label">{{$t('frequency_discount')}}</span>
                                        <span style="color: var(--color-primary);">-{{ currencySymbol }}{{ Number(TotalFrequencyDiscount).toFixed(2) }}</span>
                                    </div>
                                    <div v-else>
                                        <span >Frequency Fee</span>
                                        <span style="color: var(--color-primary);">{{ currencySymbol }}{{ Number(TotalFrequencyDiscount).toFixed(2) | absolute }}</span>
                                    </div>
                                </div>

                                <!-- Gift Card -->
                                <div v-if="gift_card_amount > 0">
                                    <div class="pricing-item">
                                        <span class="label">{{$t('gift_card')}}</span>
                                        <span style="color: var(--color-primary);">-{{ currencySymbol }}{{ gift_card_amount.toFixed(2) }}</span>
                                    </div>
                                    <div class="pricing-item selected-giftcard">
                                        <span class="label">{{ selected_giftcard.code }}</span>
                                        <div class="remove-discount" @click="removePromoCode()">
                                            <img src="/images/booking-form/trash.svg" alt="Remove Discount">
                                        </div>
                                    </div>
                                </div>

                                <!-- Discount Code -->
                                <div v-if="selected_discount">
                                    <div class="pricing-item">
                                        <span class="label">{{$t('discount_code')}}</span>
                                        <!-- Amount -->
                                        <span style="color: var(--color-primary);">-{{ currencySymbol }}{{ Number(TotalPromoDiscount).toFixed(2) | absolute }}</span>
                                    </div>
                                    <div class="pricing-item" style="margin-top: -5px;">
                                        <div class="center-discount">
                                            <span class="selected-discount">{{ selected_discount.code }}</span>

                                            <span class="discount-rate" v-if="selected_discount.percent">-{{ selected_discount.percent }}%</span>

                                            <div class="remove-discount" @click="removePromoCode()">
                                                <img src="/images/booking-form/trash.svg" alt="Remove Discount">
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Same Day Booking Fee -->
                                <div v-if="additionalFee && same_day_upcharge">
                                    <div class="pricing-item">
                                        <span class="label">{{ settings.fee_day_label }}</span>
                                        <span>{{ currencySymbol }}{{ same_day_upcharge.toFixed(2) }}</span>
                                    </div>
                                </div>

                                <!-- Sales Tax -->
                                <div v-if="salesTax && salesTax.tax_enabled === 1">
                                    <div class="pricing-item">
                                        <span class="label">{{ salesTax.tax_label }}</span>
                                        <span>{{ currencySymbol }}{{ taxAmount.toFixed(2) }}</span>
                                    </div>
                                </div>

                            </div>

                            <!-- TO DO Referral program -->
                            <div v-if="customer && settings.referral_program_enabled" class="referrals">

                                <div class="pricing-item">
                                    <span class="balance-label">
                                        {{$t('referral_balance')}}
                                    </span>
                                    <span class="balance-total">{{ currencySymbol }}{{ customer.referrals_balance.toFixed(2)}}</span>
                                </div>

                                <div class="pricing-item">
                                    <span class="apply">
                                        <el-checkbox style="color:#606266 !important; align-items: center;" v-model="referral_use_balance">{{$t('apply_balance')}}</el-checkbox>
                                    </span>
                                    <el-input-number controls-position="right" size="small" v-model="referral_amount_to_use" :min="0" :max="referral_max_amount_to_use" :precision="2" :style="{ color: referral_use_balance ? 'var(--color-primary)': '' }">
                                    </el-input-number>
                                </div>

                            </div>

                            <!-- Total -->
                            <div v-if="clientId != 3329" class="total">
                                <div class="total-label">Total</div>
                                <div v-if="totalPrice>=0" class="total-amount">{{ currencySymbol }}{{ totalPrice.toFixed(2) }}</div>
                            </div>

                        </div>

                    </el-col>

                </el-row>

                <!-- Thank You Page -->
                <el-row v-else>
                    <thankyoupage :client="clientId" :company_name="company_name">
                        <template v-if="existPaymentMethods" v-slot:bookingsLink>

                            <el-button v-if="$guardModel=='customer'" type="primary" class="history-link" @click="$emit('changeTab', 'bookings', null)">View History</el-button>
                            <el-button v-if="$guardModel=='member'" type="primary" class="history-link mt-4" @click="$router.push({name:'viewCustomer', params: { id:customer.id }})" style="cursor:pointer;">Back to Customer</el-button>

                        </template>
                    </thankyoupage>
                </el-row>

            </el-form>
        </div>
    </div>
</template>

<script>

import moment from 'moment'
import phoneValidator from 'phone'

import vidThumbnail from './assets/bookingform_thumbnail.png'

import { Stripe, baseStyle } from '@components/vue-stripe-elements-plus/src/stripeElements'
import Card from '@components/vue-stripe-elements-plus/src/Card.vue'

import Extra from './src/ExtraV2.vue'
import Service from './src/Service.vue'


import Frequency from './src/FrequencyV2.vue'
import cCardBrands from '@components/c-card-brands-v2/index.vue'
import Divider from './src/Divider.vue'

export default {

    props: {

        clientId      : {
            type      : String,
            required  : true
        },
        locationId : {
            type      : String,
            required  : true
        },
        stripeKey     : {
            type      : String,
            required  : true
        },
        customer: {
            type: Object
        },
        bookInfo:{
            type: Object
        },
        customizer:{
            type: Object,
            default: () => ({
                appearance: {
                    formStyle: "default_new",
                    accentColor: "#4285f4",
                    servicesStyle: "default",
                    formBorderRadius: 30,
                    inputBorderRadius: 12
                }
            })
        }
    },

    components: {
        Card,
        Extra,
        Frequency,
        Thankyoupage:  () => import('./src/Thankyoupage.vue'),
        PaymentMethods:  () => import('../customers/components/paymentMethods/list.vue'),
        cCardBrands,
        Divider,
        Service
    },

    filters: {
        absolute(value)
        {
            return Math.abs(Number(value))
        }
    },

    data() {

        var checkNames = (rule, value, callback) => {
            var RegExpression = /^[a-zA-Z\s']*$/
            if(RegExpression.test(value)) {
                callback()
            } else {
                callback(new Error('Name cannot contain numbers or symbols'))
            }
        }

        var checkPhone = (rule, value, callback) => {
            if (value) {
                const cleanValue = value.replace(/\D/g, '');

                if (!/^\d+$/.test(cleanValue)) {
                    callback(new Error('Phone number can only contain digits'));
                    return;
                }

                this.bookForm.phone = cleanValue;

                let countryCode = this.serviceLocale ? this.serviceLocale.short_name : 'US';

                let validationResult = phoneValidator(cleanValue, countryCode, true);

                if (validationResult.length) {
                    callback();
                } else {
                    callback(new Error('Invalid Phone Number'));
                }
            } else {
                callback(new Error('Phone Number is required'));
            }
        };

        return {
            queryParams: null,
            first_time_client: false,
            num: 0,
            faq               : [],
            coordinates: {
                lat: 0,
                lng: 0
            },
            checked           : this.clientId == 3151 ? false : true,
            done              : false,
            error             : false,
            rules             : {
                first_name     : [
                    { required: true, message: 'First Name is required', trigger: ['blur', 'change'] },
                    { validator: checkNames, trigger: ['blur', 'change'] }
                ],
                last_name      : [
                    { required: true, message: 'Last Name is required', trigger: ['blur', 'change'] },
                    { validator: checkNames, trigger: ['blur', 'change'] }
                ],
                email          : [
                    { required: true, message: 'Email Address is required', trigger: ['blur', 'change'] },
                    { type: 'email', message: 'Please insert a valid email address', trigger: ['blur', 'change'] }
                ],
                phone          : [
                    { required: true, validator: checkPhone, trigger: ['blur', 'change'] },
                ],
                city           : [
                    { required: true, message: 'City is required', trigger: ['blur', 'change'] }
                ],
                street_address : [
                    { required: true, message: 'Street Address is required', trigger: ['blur', 'change'] }
                ],
                selected_state : [
                    { required: true, message: 'State is required', trigger: ['blur', 'change'] },
                ],
                zipcode        : [
                    { required: true, message: 'Zip Code is required', trigger: ['blur', 'change'] },
                    { min: 2, max: 15, message: 'Zip Code length error', trigger: ['blur', 'change'] }
                ],
                selected_date  : [
                    { required: true, message: 'Date is required', trigger: 'change' }
                ],
                selected_hour  : [
                    { required: true, message: 'Time is required', trigger: 'change' }
                ],
                /*terms          : [
                    { validator: checkTerms, trigger: 'change' },
                ],*/
            },
            stripe            : null,
            complete          : false,
            bookForm          : {
                city               : '',
                email              : '',
                hours              : [],
                phone              : '',
                extras             : null,
                zipcode            : '',
                services           : [],
                pricings           : null,
                time_hour          : 'AM',
                last_name          : '',
                first_name         : '',
                categories         : null,
                apt_number         : '',
                total_extra        : 0,
                total_price        : 0,
                total_service      : 0,
                total_pricing      : 0,
                selected_date      : null,
                selected_hour      : null,
                street_address     : '',
                selected_state     : null,
                selected_extras    : [],
                frequency_id   : 0,
                service_id   : 0,
                selected_extras_names : [],
                additionalInformation : '',
                promo_code            : '',
                category_id : 0,
            },
            salesTax          : null,
            settings          : {},
            taxAmount         : 0,
            promo_code        : '',
            book_intent       : '',
            priceBoxTop       : 10,
            priceBoxOffset    : 600,
            customer_id       : '',
            company_name      : '',
            spinner_code      : false,
            disabledDates     : [],
            disabledDatesarray: [],
            pickerOptions     : {  disabledDate: this.isDateBlocked },
            stripeOptions     : {
                hidePostalCode: true,
                elements      : {
                    fonts  : [
                        {
                            // integrate your font into stripe
                            cssSrc: 'https://fonts.googleapis.com/css?family=Inter:400,500',
                        }
                    ],
                    locale : 'en'
                },
                style: {
                    base: {
                        color          : '#606266',
                        fontSize       : '14px',
                        fontFamily     : "'Inter', sans-serif",
                        fontWeight     : '400',
                        lineHeight     : this.$guardModel === 'customer' ? '38px': '44px',

                        '::placeholder': {
                            color         : '#C0C4CC',
                            fontSize      : '14px',
                            fontWeight    : '400',
                            fontFamily    : "'Inter', sans-serif",
                            textTransform : 'capitalize',
                        }
                    },

                    invalid: {
                        color     : '#fa755a',
                        iconColor : '#fa755a'
                    }
                }
            },
            serviceLocale     : {
                states:null,
                currency:{}
            },
            discount_text     : 'Apply',
            cleaningPrice     : 0,
            array_holidays    : [],
            isStripeLoaded    : false,
            isFormDataLoaded : false,
            additional_fee    : false,
            promo_code_bkup   : '',
            spinner_actived   : false,
            selected_workers  : '',
            selected_pricing  : [],
            gift_card_amount  : 0,
            gift_card_balance : 0,
            selected_discount : '',
            selected_giftcard : '',
            same_day_upcharge : 0,
            selected_workers_time  : '',
            selected_discount_bkup : '',
            team_available_hours: [],
            referral_use_balance: true,
            referral_max_amount_to_use: 0,
            referral_amount_to_use: 0,
            customFields: [],
            customFieldResponses: {},
        }

    },

    beforeCreate()
    {
        // Dynamic Stripe load
        var app = this

        let stripe = document.createElement('script')
        stripe.src = 'https://js.stripe.com/v3/'
        stripe.defer = true;
        stripe.onload = () => {
            app.isStripeLoaded = true
        }
        document.body.appendChild(stripe)

    },

    created()
    {
        this.$i18n.mergeLocaleMessage(this.$i18n.locale, this.customizer?.wording)

        let locationNew = (this.locationId !== undefined && this.locationId > -1)? this.locationId : 0

        // Listen for PostMessage with query string params on the parent to prefill booking form
        window.addEventListener('message', this.listenParentMessages)

        this.$http({
            url: `/booking/booking_form/get_data/` + this.clientId + '/' + locationNew,
            // headers: {"Access-Control-Allow-Origin": "*"}

        }).then((res) => {

            const { faq, salesTax, settings, company_name, serviceCurrency, categories, services, workSchedules, customizer, customFields } = res.data
            this.faq                  = faq
            this.salesTax             = salesTax
            this.settings             = settings
            this.company_name         = company_name
            this.serviceLocale        = serviceCurrency
            this.bookForm.categories  = categories
            this.bookForm.services    = services

            // Initialize custom fields
            this.customFields = customFields || []
            this.initializeCustomFieldResponses()

            // Quick fix for skip payment in customer portal
            this.customizer.skip_payment_requirement = customizer.skip_payment_requirement


            this.bookForm.hours       = workSchedules
            this.bookForm.category_id = (this.bookForm.categories.find(category => category.id === this.bookInfo?.category_id) || categories[0])?.id

            this.prefillCustomer()
            this.loadBlockedDates()

            this.$nextTick(() => {
                this.$emit('ready')
                this.isFormDataLoaded = true

                if(this.queryParams)
                    this.prefillQuery(this.queryParams)
            })

        }, () => {
            this.$notify.error({
                title: 'Error',
                message: 'There was an error retrieving Booking form data',
                offset: 100
            })
        })



    },

    watch: {

        'customizer.appearance': {
            deep:true,
            immediate: true,
            handler(){

                if(this.customizer.appearance?.accentColor){
                    document.querySelector(":root").style.setProperty('--color-primary', this.customizer.appearance.accentColor);
                    document.querySelector(":root").style.setProperty('--input-border-radius', this.customizer.appearance.inputBorderRadius+'px');
                    document.querySelector(":root").style.setProperty('--form-border-radius', this.customizer.appearance.formBorderRadius+'px');
                }
            }
        },

        frequency_selected(after, before)
        {
            var app = this


            if(app.frequency_selected && this.selected_discount.stackable == 0){
                this.removePromoCode()
                app.$notify.warning({
                    title   : 'Warning',
                    message : "Your discount code is not stackable",
                    offset  : 100
                })
            }



            if(app.frequency_selected && app.promo_code_bkup){

                if(!app.promo_code && app.frequency_selected.interval == 'one_time'){
                    app.promo_code        = app.promo_code_bkup
                    app.selected_discount = app.selected_discount_bkup
                }

                if(app.promo_code && app.frequency_selected.interval != 'one_time'){
                    app.promo_code        = ''
                    app.selected_discount = ''

                    app.$notify.warning({
                        title   : 'Warning',
                        message : "Your promo code is just for One Time Service",
                        offset  : 100
                    })
                }
            }
        },

        selected_category(after, before)
        {
            this.$nextTick(()=>{
                if(this.selected_category) {
                    this.$set(this.bookForm, 'services', this.selected_category.services);
                    // this.bookForm.service_id = this.selected_category.services[0].id || []
                }
            })
        },

        selected_service(after, before)
        {
            var app = this

            if (app.settings.zero_discount_to_first_booking == 1 && app.bookForm.email.length > 0)
            {
                app.checkFirstTimeClient()
            }

            //doing a copy on parallel

            let extras_copy = app.bookForm.extras
            let parallel_extras = app.bookForm.selected_extras

            app.selected_pricing                = []
            app.selected_workers                = ''
            app.selected_workers_time           = ''
            app.bookForm.selected_extras        = []
            app.bookForm.selected_extras_names  = []

            if(app.selected_service){

                app.bookForm.extras        = app.selected_service.service_extras.length   ? app.selected_service.service_extras   : null

                /*
                    if we have selected extras then compare
                    which ones of the previously selected are in
                    the new set of extras so we can preseve them
                */
                if (parallel_extras.length > 0) {
                    parallel_extras.forEach(element => {
                       let filtered_extra = app.bookForm.extras.filter(service_extra => service_extra.id_extra == element)
                       if (filtered_extra.length > 0)
                       {
                            let parallel_copy = extras_copy.filter(service_extra => service_extra.id_extra == element)
                            app.toggleExtra(parallel_copy[0].extra.quantity, filtered_extra[0].extra)
                       }
                    })
                }

                if (app.selected_service.pricings) {
                    app.bookForm.pricings  = app.selected_service.pricings

                } else {
                    app.bookForm.pricings = null
                }

                app.bookForm.total_extra   = 0
                app.bookForm.total_pricing = 0

                if(app.selected_service.groups && app.bookForm.pricings) {

                    app.selected_service.groups.forEach(
                        group => {
                            app.bookForm.pricings.forEach(
                                pricing_parameter => {

                                    if (pricing_parameter.group == group && !app.selected_pricing[group]) {

                                        app.selected_pricing[group] = pricing_parameter
                                    }
                                }
                            )
                        }
                    )
                }

                if(app.selected_service.is_hourly){
                    app.selected_workers      = app.selected_service.cleaners[0]
                    app.selected_workers_time = app.selected_service.hours[0]
                }
            }

        },

        selected_pricing(after, before)
        {
            var app = this

            if(app.selected_pricing){
                app.bookForm.total_pricing = 0

                app.selected_pricing.forEach(
                    element => {
                        if(element.price)
                            app.bookForm.total_pricing += Number(element.price)
                    }
                )
            }

            this.$nextTick(()=>{
                this.referral_amount_to_use = this.referral_max_amount_to_use.toFixed(2)

            })


        },

        selected_workers(after, before)
        {
            var app = this
            app.bookForm.total_service = 0

            if(app.selected_service){
                app.bookForm.total_service = Number(app.selected_service.price) * Number(app.selected_workers_time) * Number(app.selected_workers)
            }
        },

        selected_workers_time(after, before)
        {
            var app = this
            app.bookForm.total_service = 0

            if(app.selected_service){
                app.bookForm.total_service = Number(app.selected_service.price) * Number(app.selected_workers_time) * Number(app.selected_workers)
            }
        },

        customFields: {
            handler() {
                this.initializeCustomFieldResponses()
            },
            deep: true
        },
    },

    methods: {

        prefillQuery(query){

            var aux_selected = []

            Object.keys(query).forEach((key) =>{
                this.$set(this.bookForm, key, (key === 'category_id' || key === 'service_id') ? Number(query[key]) : query[key])
            })

            this.$nextTick(tick => {

                Object.keys(query).forEach((key) => {

                    let param = query[key];
                    //   pricing_parameter[group] = pp_id
                    // [gruop_id, pricing_id]
                    var [name, index] = key.split('=')[0].split(/[\[\]]+/)

                    if(name == 'pricing_parameter'){


                        const aux   = this.selected_service.pricings.find(pricing =>{
                            return (pricing.group == index) && (pricing.id == param)
                        })

                        if(aux)
                            aux_selected[index] = aux
                    }

                })

                if(aux_selected.length > 0)
                    this.selected_pricing = (aux_selected)

            })


            this.promo_code = this.bookForm.promo_code

            if (this.promo_code.length > 0) {
                this.promoCode(0);
            }

        },


        setPlace(place) {

            if (!place?.geometry) {
                this.bookForm.street_address = place.target.value
                return;
            }

            this.bookForm.street_address = place.name;
            place.address_components.forEach((component) => {
                if (component.types.includes('locality')) {
                    this.bookForm.city = component.long_name;
                }
                if (component.types.includes('administrative_area_level_1')) {
                    this.bookForm.selected_state = component.short_name;
                }
                if (component.types.includes('postal_code')) {
                    this.bookForm.zipcode = component.long_name;
                }
            });
            //update the coordinates
            this.coordinates.lat = place.geometry.location.lat();
            this.coordinates.lng = place.geometry.location.lng();

        },


        checkFirstTimeClient()
        {

            this.$http({
                url: `booking/settings/check_first_time_client`,
                method: 'POST',
                data: {
                    client_id: this.clientId,
                    email : this.bookForm.email,
                }
            }).then((res) => {
                // console.log('getting here')
                // console.log(res.data)
                this.first_time_client = res.data.new_client


            }, (res) => {

            })

        },

        handleChange(value) {
            console.log(value);
        },

        // Get any date into the desired format
        formatDate(date, delta = null){
            return moment(date).add(delta).format("YYYY-MM-DD")
        },

        // Validate number input
        isNumber(evt) {
            const keysAllowed= ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'ArrowLeft', 'ArrowRight'];
            const keyPressed= evt.key;
            if (!keysAllowed.includes(keyPressed)) {
                evt.preventDefault()
            }
        },

        // Load dates restrictions - Holidays and Custom Blocked Dates
        loadBlockedDates(){

            this.$http({ url: `/booking/blocked_dates/` + this.clientId }).then((res) => {

                this.disabledDatesarray = res.data

            }, () =>{
                this.$notify.error({
                    title: 'Error',
                    message: 'There was an error retrieving Holidays',
                    offset: 100
                })
            })
        },

        // Check if the specified date should be blocked on date picker
        isDateBlocked(date){

            let dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()]

            /** HeadsUp Time
             *  now() + headsUp and check if the result is greater than picker date
             */
            let headsUp = moment().add(this.headsupHours, 'hours').startOf('day')
            if( date < headsUp)
                return true

            /** Business Hours
             *  Check if the business has available hours for the date picker day
             */
            if(this.bookForm.hours[dayName].length < 1)
                return true;

            /** Blocked Dates
             *  Check if the datepicker day is within custom blocked / holidays list
             */
            if(this.disabledDatesarray.indexOf( this.formatDate(date) ) > -1){
                return true
            }


        },

        // Prefill Customer/Booking info when customer|bookinfo props exist.
        prefillCustomer()
        {
            var app = this;

            // Prefill customer info
            if (app.customer) {
                // Remove phone prefix if it exists
                if (app.customer.phone && app.serviceLocale.phone_prefix) {
                    if (app.customer.phone.startsWith(app.serviceLocale.phone_prefix)) {
                        app.customer.phone = app.customer.phone.replace(app.serviceLocale.phone_prefix, '');
                    }
                }
                
                app.bookForm = {...app.bookForm, ...app.customer}
            }

            app.$nextTick(tick => {

                if (app.bookInfo) {

                    // Services and address Prefill
                    let address = {...app.bookInfo.address}
                    app.bookForm = {...app.bookForm, selected_state: address.state,  ...app.bookInfo.address, frequency_id: app.bookInfo.frequency_id, category_id: app.bookInfo.category_id,  service_id: app.bookInfo.service_id, additionalInformation: app.bookInfo.additional_information}

                    // if (app.settings.zero_discount_to_first_booking == 1)
                    // {
                    //     app.checkFirstTimeClient()
                    // }

                    app.$nextTick(tick => {

                        // Pricing Parameters Prefill
                        let aux_selected = [];
                        Object.keys(app.bookInfo.pricings).forEach(function(key){

                            var param = app.bookInfo.pricings[key];
                            const aux = app.selected_service.pricings.find(pricing =>{
                                return (pricing.id == param.id_pricing_attribute)
                            })

                            if(aux)
                                aux_selected[aux.group] = aux
                        })

                        // prefill hourly data
                        if(app.selected_service.is_hourly){
                            let hourly = JSON.parse(app.bookInfo.hourly)
                            app.selected_workers      = hourly.workers
                            app.selected_workers_time = hourly.workers_time
                        }

                        if(aux_selected.length > 0)
                            app.selected_pricing = (aux_selected)

                        // Extras Prefill
                        Object.keys(app.bookInfo.extras).forEach(function(key){
                            var extra = app.bookInfo.extras[key]
                            app.toggleExtra(extra.quantity, extra.extra)
                        })

                        app.prefillExtras()
                    })



                }
            })
        },

        prefillExtras() {

            var app = this;
            let extraList = []
            let extras = app.bookForm.extras;

            (extras)?.forEach(element => {
                if (this.isAdded(element.id_extra)) {
                    let newExtra = element
                    newExtra.extra = app.bookForm.selected_extras_names.find(extra => extra.id == element.id_extra)
                    extraList.push(newExtra)
                } else {
                    extraList.push(element)
                }
            });


            app.bookForm.extras = extraList.length ? extraList : null

        },

        // Arrival format
        hourLabel(hour)
        {

            var app         = this,
                timeofday   = app.timeOfDay(hour),
                label       = app.$moment(timeofday).format('hh:mm a'),
                timeArrival = (app.timeArrival() > 0)? app.timeArrival() : 0,
                hourdate1   = app.$moment(timeofday).format('YYYY-MM-DD')

            if(timeArrival > 0){
                var hour2     = app.$moment(timeofday).add(timeArrival, 'hour').format('hh:mm a'),
                    hourdate2 = app.$moment(timeofday).add(timeArrival, 'hour').format('YYYY-MM-DD')

                // If after adding arrival window the second date is the day after, ommit.
                if(hourdate1 != hourdate2 )
                    return false

                label = label + ' - ' + hour2
            }

            return label
        },

        // Helper to let moment transform 24hr time to AM/PM with a valid date
        timeOfDay(time)
        {
            const date = "1970-01-01"
            return `${date} ${time}`
        },

        // Return the placeholder for the specified input name, if not found return default
        _placeholder(name)
        {
            if(this.serviceLocale.bookForm !== undefined)
                return this.serviceLocale.bookForm[name] || false
        },

        // replicate the scroll of the parent so the summary box moves down
        listenParentMessages(event)
        {
            // Scroll Price box Attempts
            if(event.data.type == 'scroll' ){

                if ('parentIFrame' in window) {
                    parentIFrame.getPageInfo(
                        (pageInfo) =>  {
                            this.priceboxOffset = event.data.top - pageInfo.offsetTop
                        })
                }

                let bounds = document.getElementById('imageboxDiv').getBoundingClientRect();

                if (Math.abs(this.priceboxOffset - bounds.bottom ) >= 2) {
                        this.priceBoxTop =  this.priceboxOffset
                }
            }

            if(event.data.type == 'setParams'){
                if(this.isFormDataLoaded){
                    this.prefillQuery(event.data.params)
                }else{
                    this.queryParams = event.data.params
                }
            }
        },
        perCleaning()
        {
            return (this.cleaningPrice.toFixed(2) - this.bookForm.total_extra.toFixed(2)).toFixed(2)
        },

        // Apply Promo Code
        promoCode(alert)
        {
            var app = this

            app.spinner_code = true
            app.$http({
                url: `booking/promo/check`,
                method: 'POST',
                data: {
                    code      : app.promo_code,
                    email     : (app.bookForm.email) ? app.bookForm.email : '',
                    amount    : app.totalPrice,
                    member_id : app.clientId
                }
            }).then((res) => {

                var show_alert   = true
                var status_text  = res.data.status
                app.spinner_code = false

                if(res.data.valid) {

                    app.gift_card_amount  = res.data.available
                    app.gift_card_balance = res.data.balance

                    if(res.data.type == 'discount'){

                        if(res.data.object.stackable === 0 && this.TotalFrequencyDiscount > 0){

                            this.removePromoCode()
                            app.$notify.warning({
                                title   : 'Warning',
                                message : "Your promo code is not stackable with frequency discounts",
                                offset  : 100
                            })
                            return;
                        }

                        app.selected_discount      = res.data.object

                        if(app.selected_discount.one_time_service == 1){
                            app.promo_code_bkup        = app.promo_code
                            app.selected_discount_bkup = res.data.object

                            if(app.frequency_selected.interval != 'one_time'){
                                app.promo_code        = ''
                                app.selected_discount = ''

                                app.$notify.warning({
                                    title   : 'Warning',
                                    message : "Your promo code is just for One Time Service",
                                    offset  : 100
                                })
                                show_alert = false
                            }
                        }

                    }

                    if(res.data.type == 'giftCard'){
                        app.selected_giftcard = res.data.object
                    }

                    if(alert && show_alert)
                        app.$notify.success({
                            title: 'Success',
                            message: status_text,
                            offset: 100
                        })

                }else{
                    app.promo_code        = ''
                    app.selected_giftcard = ''
                    app.selected_discount = ''

                    if(alert)
                        app.$notify.warning({
                            title: 'Warning',
                            message: status_text,
                            offset: 100
                        })
                }

            }, (res) => {
                app.spinner_code = false
                app.$notify.error({
                    title: 'Error',
                    message: res.data.error,
                    offset: 100
                })
            })

        },

        promoExist()
        {
            if(this.selected_discount || this.selected_giftcard)
                return true

            return false
        },

        removePromoCode()
        {
            this.promo_code        = ''
            this.gift_card_amount  = 0
            this.gift_card_balance = 0

            if(this.selected_giftcard)
                this.selected_giftcard = ''

            if(this.selected_discount)
                this.selected_discount = ''
        },

        label_time(time)
        {
            var label = time + " Hour"

            if(time > 1)
                label += 's'

            return label
        },

        label_workers(workers) {

            const workerLabel = this.$t('worker_label');

            var label = workers + " " + workerLabel;

            if(workers != 1)
                label += 's';

            return label;
        },

        bookStore()
        {
            var app = this
            var payment_method = (app.book_intent.setupIntent?.payment_method)? app.book_intent.setupIntent?.payment_method : ''
            let phoneValidated = phoneValidator(app.phoneWithPrefix,'',true)
            app.$http({
                url: `booking/book`,
                method: 'POST',
                data: {
                    city             : app.bookForm.city,
                    hour             : app.bookForm.selected_hour,
                    state            : app.bookForm.selected_state,
                    email            : app.bookForm.email,
                    phone            : phoneValidated[0],
                    extras           : app.bookForm.selected_extras,
                    zipcode          : app.bookForm.zipcode,
                    workers          : app.selected_workers,
                    pricings         : app.selected_pricing,
                    sales_tax        : (app.salesTax && app.salesTax.tax_enabled == 1) ? app.salesTax.id : null,
                    client_id        : app.clientId,
                    location_id      : app.locationId,
                    last_name        : app.bookForm.last_name,
                    first_name       : app.bookForm.first_name,
                    service_id       : app.selected_service.id,
                    apt_number       : app.bookForm.apt_number,
                    customer_id      : app.customer_id,
                    discount_id      : (app.selected_discount) ? app.selected_discount.id : null,
                    gift_card_id     : (app.selected_giftcard) ? app.selected_giftcard.id : null,
                    workers_time     : app.selected_workers_time,
                    frequency_id     : app.frequency_selected.id,
                    extras_names     : app.bookForm.selected_extras_names,
                    selected_date    : app.bookForm.selected_date,
                    street_address   : app.bookForm.street_address,
                    payment_method   : payment_method,
                    coordinates      : app.coordinates,
                    additional_fee   : app.additional_fee,
                    additionalInfo   : app.bookForm.additionalInformation,
                    gift_card_amount : app.gift_card_amount,
                    category_id      : (app.selected_category && app.selected_category.id)?app.selected_category.id:0,
                    referral_use_balance: app.referral_use_balance,
                    referral_amount_to_use: app.referral_amount_to_use,
                    custom_field_responses: app.prepareCustomFieldResponses()
                }
            }).then((res) => {
                app.error           = false
                app.done            = true
                app.spinner_actived = false

                // if customer refresh customer data
                if(this.customer){
                    this.$store.dispatch('refresh_customer_data')
                }

            }, (error) => {
                app.spinner_actived = false
                app.$notify.error({
                    title: 'Error',
                    message: error.response.data.message || 'There was an error while placing the booking. Contact us for more information',
                    offset: 100,
                    position: 'bottom-right'
                })
            })

        },

        book()
        {
            let app  = this
            app.spinner_actived = true

            this.$refs['bookForm'].validate((valid, errors) => {
                if (valid) {

                    if (app.settings.zero_discount_to_first_booking == 1)
                    {
                        app.checkFirstTimeClient()
                    }

                    app.error = false

                    // Without Credit Card
                    if (app.customizer.skip_payment_requirement) {
                        app.bookStore()
                    }

                    // With Credit Card
                    else {
                        app.$http({
                            url: `booking/create_intent`,
                            method: 'POST',
                            data: {
                                email       : app.bookForm.email,
                                member_id   : app.clientId,
                                location_id : app.locationId,
                            }

                        }).then(async (res) => {


                            /**
                             * If existPaymentMethods use the PaymentMethods component, if not use the normal card component.
                             * the component PaymentMethods has a method called getPaymentMethodId that returns a promise
                             * with the same response structure than Stripe.handleCardSetup.
                             */
                            let card_promise = this.existPaymentMethods ?
                                this.$refs.PaymentMethods.getPaymentMethodId(res.data.client_secret) : Stripe.handleCardSetup(res.data.client_secret)

                            card_promise.then(data => {

                                if (data.setupIntent != undefined) {
                                    app.customer_id = data.setupIntent.customer || res.data.customer_id
                                    app.book_intent = data

                                    app.bookStore()

                                } else {

                                    app.spinner_actived = false
                                    app.$notify.error({
                                        title: 'Error',
                                        message: data.error.message,
                                        offset: 100,
                                        position: 'bottom-right'
                                    })
                                }
                            })


                        }, (res) => {
                            app.spinner_actived = false
                            app.$notify.error({
                                title   : 'Error',
                                message : res.response.data.error,
                                offset  : 100,
                                position: 'bottom-right'
                            })
                        })
                    }

                } else {
                    app.error           = true
                    app.spinner_actived = false
                    app.$nextTick(() => {
                        document.querySelectorAll('.el-form-item__error')[0].closest(".section-separator").scrollIntoView()
                    })

                    return false
                }
            })

        },

        isAdded(id)
        {
            if(this.bookForm.selected_extras)
                return this.bookForm.selected_extras.indexOf(id) !== -1
        },

        toggleExtra(counter, extra)
        {
            var app = this
            const index = app.bookForm.selected_extras.indexOf(extra.id)

            if (counter >= 1) {

                extra.quantity = counter

                if (index == -1) {
                    app.bookForm.selected_extras.push(extra.id)
                    app.bookForm.selected_extras_names.push(extra)
                }else{
                    app.$set(app.bookForm.selected_extras_names, index, extra)
                }

            } else {
                if (index != -1) {
                    app.bookForm.selected_extras.splice(index, 1)
                    app.bookForm.selected_extras_names.splice(index, 1)
                }
            }
        },

        discountAmount()
        {
            if (this.frequency_selected) {
                return Number(this.frequency_selected.amount)
            }

            return 0
        },

        discountPercent()
        {
            if (this.frequency_selected) {
                return Number(this.frequency_selected.percent)
            }

            return 0
        },

        feeAmount()
        {
            if (this.settings) {
                return Number(this.settings.fee_day_amount)
            }

            return 0
        },

        timeArrival()
        {
            var app = this

            if (app.settings) {
                return Number(app.settings.time_arrival)
            }

            return 0
        },

        feePercent()
        {
            if (this.settings) {
                return Number(this.settings.fee_day_percent)
            }

            return 0
        },

        discountCodeAmount()
        {
            if (this.selected_discount) {
                return Number(this.selected_discount.amount)
            }

            return 0
        },


        discountCodePercent()
        {
            if (this.selected_discount) {
                return Number(this.selected_discount.percent)
            }

            return 0
        },

        // Initialize custom field responses based on field types
        initializeCustomFieldResponses() {
            const responses = {}
            this.customFields.forEach((field, index) => {
                if (field.type === 'checkbox') {
                    responses[index] = false
                } else {
                    responses[index] = ''
                }
            })
            this.$set(this, 'customFieldResponses', responses)
        },

        // Prepare custom field responses for submission
        prepareCustomFieldResponses() {
            const responses = []
            
            this.customFields.forEach((field, index) => {
                const response = this.customFieldResponses[index]
                
                // Only include non-empty responses
                if (response && response !== '' && response !== false) {
                    responses.push({
                        label: field.label,
                        type: field.type,
                        response: field.type === 'checkbox' ? (response ? 'Yes' : 'No') : response
                    })
                }
            })
            
            return responses
        },
    },

    computed: {

        vidThumb(){

            if(typeof this.customizer?.appearance?.service_banner !== 'undefined')
                return this.customizer?.appearance?.service_banner
            else return  vidThumbnail
        },

        // Current selected service and extras time
        getTeamAvailability(){

            let total_time = 0;

            if(this.selected_service){

                // service time
                total_time = this.selected_service.is_hourly ? parseFloat(this.selected_workers_time * 60) * this.selected_workers : this.selected_service.time_duration

                // pricings total time
                this.selected_pricing.forEach((pricing) => {
                    total_time += pricing['time_duration'];
                })

                // extras total time
                this.bookForm.selected_extras_names.forEach((extra) => {
                    total_time += (extra.quantity) * ((extra.time_duration_hours * 60) + extra.time_duration_minutes)
                })
            }

            this.$http({
                url: `booking/availability/` + this.clientId,
                method: 'GET',
                params: {
                    service_date: this.bookForm.selected_date,
                    duration: total_time,
                    location_id: this.locationId
                }
            }).then((res)=>{
                this.team_available_hours = res.data
            });

        },

        headsupHours(){
            return this.settings.hours_to_request + (this.settings.mins_to_request / 60)
        },

        existPaymentMethods()
        {
            return this.customer ? true : false
        },

        arrivalLabel()
        {

            let timeArrival = (this.timeArrival() > 0)? this.timeArrival() : 0;

            if (timeArrival > 0) {

                return this.$t('placeholder_arrival')
            }

            return this.$t('placeholder_time')
        },

        currentDay(){


            let start_end   = [];
            let selectedDate = this.$moment(this.bookForm.selected_date);
            let currentHour   = this.$moment().format('HH:mm:ss');
            let numberDay   = selectedDate.format('dddd');

            let use_team_availability = !!this.settings.use_team_availability_on_booking_form;
            let selectedDateIsToday = selectedDate.isSame(Date.now(), 'day');


            // Workaround to let the getteam availability be called only if setting is on, DONT REMOVE
            let aux = (use_team_availability)? this.getTeamAvailability : null;

            let hours_to_check = this.settings.hours_to_request + (this.settings.mins_to_request / 60)

            var sel = selectedDate.endOf('day'),
                end = this.$moment().add(hours_to_check, 'hour'),

                duration  = this.$moment.duration(end.diff(sel)),
                diffhours = duration.asHours(),
                calc      = (24 - Math.abs(diffhours))


             if(calc > 0 && hours_to_check > 0){
                 currentHour = selectedDate.add(calc, 'hour').format('HH:mm:ss')
            }

             //  Get work spots based on availability setting
             let workSpots = (use_team_availability)? this.team_available_hours : this.bookForm.hours[numberDay]

             // filter available spots
             const availableSpots = workSpots?.filter(spot => {

                 // remove past spots
                 if (selectedDateIsToday && (spot < currentHour)) return false;

                 // @TODO: check hours
                 // const isOutsideRange = start_end.every(item => {
                 //     return !(element <= item[1] && element >= item[0]);
                 // });

                 return true;

             }) || [];


             // select first available spot
             let hour = availableSpots?.length > 0 ? availableSpots[0] : null;

             // if the hour is within the time arrival
             if(this.hourLabel(hour))
                 this.bookForm.selected_hour = hour




             return availableSpots
        },

        // Return selected freqency Object based on frequency_id
        frequency_selected() {

            // Check for first-time client if zero discount is enabled
            if (this.settings.zero_discount_to_first_booking && this.bookForm.email.length > 0) {
                this.checkFirstTimeClient();
            }

            let frequency = null;

            if(this.selected_service){

                // Find frequency by ID or default
                frequency = this.selected_service.service_frequency.find(
                    freq => freq.frequency.id == this.bookForm.frequency_id
                );

                // If no frequency found, try to find default
                if (!frequency) {
                    frequency = this.selected_service.service_frequency.find(
                        freq => freq.frequency.default == true
                    );
                }

                // If still no frequency, use the first available frequency
                if (!frequency && this.selected_service.service_frequency.length > 0) {
                    frequency = this.selected_service.service_frequency[0];
                    this.bookForm.frequency_id = frequency.frequency.id;
                }
            }

            return frequency?.frequency;
        },


        selected_category() {
            return this.bookForm.categories?.find?.((category) => category.id == this.bookForm.category_id);
        },


        // Return selected service Object based on service_id
        selected_service :{

            get: function(){
                var service =  this.bookForm.services.find(
                    function(service){
                        return service.id == this.bookForm.service_id
                    }.bind(this)
                ) ||  this.bookForm.services?.[0]

                return service

            },
            set:function($value){

                this.$set(this.bookForm, 'service_id', $value.id)
            }
        },

        faqList()
        {
            let aux = []
            if(this.faq)
                this.faq.forEach( element => {
                    aux.push({
                        label: element.question,
                        children: [{
                            label: element.answer,
                        }]
                    })
                })

            return aux
        },

        phoneWithPrefix()
        {
            return this.serviceLocale.phone_prefix + this.bookForm.phone
        },

        currencySymbol()
        {
            return this.serviceLocale.currency[2]
        },

        // Same day fee
        additionalFee()
        {
            this.additional_fee    = false
            this.same_day_upcharge = 0

            if(this.bookForm.selected_date && this.bookForm.selected_hour){

                var dt1 = Date.parse(new Date())
                var date_parse = this.bookForm.selected_date + '-' + this.bookForm.selected_hour
                var a = date_parse.split(/[^0-9]/)
                var dt2 = new Date (a[0],a[1]-1,a[2],a[3],a[4],a[5])


                if((dt2 - dt1) > 0){

                    // check date against the server preventing local date tampering
                    this.$http({
                        url: `booking/settings/checkdate`,
                        method: 'POST',
                        data: {
                            date : this.bookForm.selected_date,
                        }
                    }).then((res) => {

                        if(res.data.valid && this.settings.fee_day_booking)
                            this.additional_fee = true
                    })

                }else{

                    //@TODO: remove from here, this doesnt belong here

                    // This prevents the user from select a past hour
                    this.bookForm.selected_hour = ''
                    return false
                }
            }

            return true
        },

        totalPrice()
        {
            var app = this

            if (app.selected_service) {

                app.bookForm.total_service = 0

                if (app.selected_service.is_hourly) {
                    app.bookForm.total_service = Number(app.selected_service.price) * Number(app.selected_workers_time) * Number(app.selected_workers)
                }else{
                    app.bookForm.total_service = app.selected_service.price
                }

                app.bookForm.total_service =  app.bookForm.total_service || 0

            }


            // Extras Price Calc
            app.bookForm.total_extra = app.bookForm.selected_extras_names.reduce((a, b) => a + (b.quantity * b.price), 0)

            let total = Number(app.bookForm.total_service) + Number(app.bookForm.total_pricing) + Number(app.bookForm.total_extra)

            app.cleaningPrice = total

            // Frequency Discount
            total = app.TotalFrequencyDiscount ? (total - app.TotalFrequencyDiscount) : total

            // Promo code Discount
            total = app.TotalPromoDiscount ? (total - app.TotalPromoDiscount) : total


            // Giftcard discount calculation
            if(app.gift_card_amount > 0){
                if(total <= app.gift_card_balance)
                    app.gift_card_amount = total
                else
                    app.gift_card_amount = app.gift_card_balance
            }

            total = total - app.gift_card_amount
            // end giftcard discount calculation



            // referrals discount calculation
            if(app.customer){
                if(total <= app.customer.referrals_balance)
                    app.referral_max_amount_to_use = total
                else
                    app.referral_max_amount_to_use = app.customer.referrals_balance

                // If referral balance apply checkbox is enable
                if(app.referral_use_balance){

                    // if amount input is not empty
                    if(typeof app.referral_amount_to_use !== 'undefined'){
                        total = total - app.referral_amount_to_use
                    }

                }
            }
            // end referrals discount calculation


            // Same day fee
            if(app.additional_fee){

                if(app.feePercent()){
                    app.same_day_upcharge =  (this.feePercent()/ 100) * total
                }

                if(app.feeAmount()) {
                    app.same_day_upcharge = app.feeAmount()
                }

                total += app.same_day_upcharge
            }

            // Sales Tax
            if (app.salesTax != null && app.salesTax.tax_enabled) {
                app.taxAmount =  (app.salesTax.percentage/ 100) * total
                total += app.taxAmount
            }

            return total

        },

        TotalPromoDiscount()
        {
            var app     = this
            var amount  = app.discountCodeAmount()
            var percent = app.discountCodePercent()

            if(percent)
                var calc = app.cleaningPrice * (percent / 100)

            return amount? (amount.toFixed(2)) : percent?  ( calc.toFixed(2) )  : 0
        },

        TotalFrequencyDiscount()
        {
            var app     = this
            var amount  = app.discountAmount()
            var percent = app.discountPercent()

            if (app.first_time_client && app.settings.zero_discount_to_first_booking == 1)
            {
                // console.log('is getting here')
                return 0
            }

            if(percent)
                var calc = app.cleaningPrice * (percent / 100)

            return amount? (amount.toFixed(2)) : percent?  ( calc.toFixed(2) )  : 0
        },
    }
}
</script>

<style>
    :root {
        --color-primary: rgb(74, 126, 243);
        --input-border-radius: 10px;
        --form-border-radius: 20px;
    }

    .el-picker-panel, .el-select-dropdown__empty, .el-notification__group {
        font-family: 'Inter', sans-serif;
    }

    @media (max-width: 768px) {
        .el-select-dropdown.el-popper {
            left: 24px;
            right: 24px;
        }
    }

</style>

<style lang="scss" scoped>

    // GENERAL STYLES
    @font-face {
        font-family: 'Inter';
        src: url('./assets/fonts/inter.woff2') format("woff2");
    }
    * {
        font-family: 'Inter', sans-serif;
    }
    h2 {
        font-size: 18px;
        font-weight: 600;
        text-align: center;
        margin-top: 0;
        margin-bottom: 8px;
    }
    p {
        font-size: 14px;
        font-weight: 400;
        text-align: center;
        margin: 0;
        color: #8492A6;
    }
    .form-wrapper {
        margin: 0 auto;
        display: flex;
        justify-content: center;
    }
    .booking-container {
        max-width: 1000px !important;
        height: 100%;
        width: 100vw !important;
    }
    .card {
        background-color: white;
        padding: 40px;
        border-radius: var(--form-border-radius);
        margin-bottom: 15px;
    }
    .section {
        .section-header {
            margin-bottom: 16px;
        }
    }
    .divider {
        width: 0px;
        position: absolute;
        border-left: 1px solid #E6EAF0;
        display: flex;
        height: 100%;
        right: 40%;
    }
    .column-divider {
        padding-left: 10px !important;
        padding-right: 10px !important;
    }

    // FORM ITEMS
    .el-form-item::v-deep {
        margin-bottom: 16px;

        .el-form-item__label {
            font-size: 13px;
            font-weight: 500;
            line-height: 20px;
            color: #8492A6;
            margin-bottom: 5px;
            display: flex;
            flex-direction: row-reverse;
            white-space: nowrap;

            &:before {
                margin-right: 0 !important;
                margin-left: 4px;
                color: #8492A6 !important;
            }
        }
        .el-form-item__content {
            line-height: unset;

            .el-form-item__error {
                font-size: 10px;
                padding-top: 3px;
            }
        }

        &:not(.is-error) {
            .el-input__inner {
                border-color: #E5E9EF;
            }
        }

        &.is-error {
            .el-input-group--prepend {
                .el-input-group__prepend {
                    border-color: #F56C6C;

                }
            }
            .el-form-item__content {
                .gmap-autocomplete {
                    .pac-target-input {
                        border-color: #F56C6C;
                    }
                }
            }
            .el-select {
                .el-input__inner {
                    border-color: #F56C6C;
                }
            }
        }
    }

    // INPUTS
    .el-input::v-deep {
        .el-input__inner {
            padding: 10px 16px;
            border-radius: var(--input-border-radius);
            height: 44px;
            line-height: 44px;
        }

        &.el-input-group--prepend {
            .el-input-group__prepend {
                border-radius: var(--input-border-radius) 0 0 var(--input-border-radius);
            }
            .el-input__inner {
                border-left: none;
                border-radius: 0 var(--input-border-radius) var(--input-border-radius) 0;
            }
        }
    }

    // TEXTAREAS
    .el-textarea::v-deep {
        .el-textarea__inner {
            font-family: 'Inter', sans-serif;
            border: 1px solid #E5E9EF;
            padding: 10px 16px;
            border-radius: var(--input-border-radius);
        }
    }

    // SELECTS
    .el-select::v-deep {
        width: 100%;
        margin-bottom: 0;

        .el-input__inner {
            border: 1px solid #E5E9EF;
            padding: 10px 16px;
            border-radius: var(--input-border-radius);
            height: 44px;
            line-height: 44px;
        }
    }

    // BUTTONS
    .el-button {
        border: none;
        border-radius: var(--input-border-radius);
        box-sizing: border-box;
        height: 44px;

        &.el-button--primary {
            background-color: var(--color-primary);

            &:hover {
                background-color: var(--color-primary);
            }
        }
        &.is-disabled {
            opacity: 0.4;
        }
    }

    // GOOGLE ADDRESS AUTOCOMPLETE
    .gmap-autocomplete::v-deep {
        .pac-target-input, input {
            border: 1px solid #E5E9EF;
            padding: 10px 16px;
            border-radius: var(--input-border-radius);
            box-sizing: border-box;
            font-size: 14px;
            -webkit-appearance: none;
            display: inline-block;
            height: 44px;
            outline: none;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
            width: 100%;

            &::placeholder {
                color: #606266;
                opacity: 0.5;
                font-weight: 500;
            }

        }
    }

    // CHECKBOX
    .el-checkbox::v-deep {
        color: #8492A6;
        white-space: normal;
        display: flex;
        align-items: center;

        .el-checkbox__input {
            display: flex;
            align-items: center;

            &.is-checked {
                .el-checkbox__inner {
                    background-color: var(--color-primary);
                    border-color: var(--color-primary);
                }
            }
        }

        .el-checkbox__inner {
            border-radius: 4px;
        }

        .el-checkbox__label {
            font-size: 14px;
            font-weight: 400;
        }
    }

    // SERVICES SECTION
    .services-section::v-deep {
        display: flex;
        flex-wrap: wrap;
        gap: 0 16px;

        @media (max-width: 768px) {
            flex-direction: column;
        }

        .el-form-item {
            flex: 1 0;

            .el-form-item__label {
                flex-direction: row;
            }
            .el-form-item__content {
                .el-select {
                    min-width: 200px;
                }
            }
        }
    }

    .services-images-section{
        display: grid;
        grid-gap: 16px;
        align-items: stretch;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    }

    // EXTRAS SECTION
    .extras-section {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-gap: 16px;
        align-items: stretch;

        @media (max-width: 830px) {
            grid-template-columns: repeat(3, 1fr);
        }

        @media (max-width: 420px) {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    // DATE AND TIME SECTION
    .el-date-editor::v-deep {
        .el-input__inner {
            padding-left: 42px;
        }
        .el-icon-date {
            color: var(--color-primary);
            font-weight: 600;
            margin-left: 5px;
        }
    }
    .el-time-editor::v-deep {
        .el-input__inner {
            padding-left: 42px;
        }
        .el-icon-time {
            color: var(--color-primary);
            font-weight: 600;
            margin-left: 5px;
            height: 100%;
            width: 25px;
            text-align: center;
            -webkit-transition: all .3s;
            transition: all .3s;
            line-height: 44px;
        }

    }

    // FREQUENCY SECTION
    .frequencies-section {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 16px;
        align-items: stretch;

        @media (max-width: 768px) {
            grid-template-columns: repeat(1, 1fr);
        }
    }
    .frequency-discount {
        font-weight: 600;
        color: var(--color-primary)
    }

    // CREDIT CARD DETAILS SECTION
    .stripe-card {
        border: 1px solid #E5E9EF;
        border-radius: var(--input-border-radius);
        height: 44px;
        box-sizing: border-box;
        font-size: 14px;
        line-height: 44px;
        outline: none;
        padding: 0 16px;
        margin-top: 0;
    }
    .credit-card-container::v-deep {
        display: flex;
        flex-direction: column;

        .el-form-item__label {
            flex-direction: row;
        }
    }
    .complete-booking-button {
        background-color: var(--color-primary);
        width: 100%;
        font-size: 16px;
        font-weight: 500;
        border-radius: var(--input-border-radius);
        padding: 4px 24px;
        height: 44px;
    }
    .card-brands {
        justify-content: center
    }

    // TREE
    .el-tree::v-deep {

        .el-tree-node {
            white-space: unset !important;
            margin-bottom: 16px;

            // Content (Question part)
            .el-tree-node__content {
                flex-direction: row-reverse;
                justify-content: space-between;
                align-items: start;
                height: unset;
                gap: 10px;

                .el-icon-caret-right {
                    color: var(--color-primary);
                    font-weight: 600;
                    margin-top: 3px;
                    padding: 0;
                    transition: none;
                    transform: none;

                    &:before {
                        content: "\e6d9";
                    }
                }
                .el-tree-node__label {
                    color: #6E918C;
                }
            }

            &.is-expanded {
                .el-icon-caret-right {
                    transform: none;
                    &:before {
                        content: "\e6d8";
                    }
                }
            }

            // Children (Answer part)
            .el-tree-node__children {

                .el-tree-node__label {
                    color: #424852;
                    font-size: 13px;
                    font-weight: 400;
                    line-height: 20px;
                    margin-top: 8px;
                }
                .el-tree-node__content {
                    padding-left: 0 !important;
                    height: unset !important;
                    min-height: 26px !important;
                    height: 100% !important;
                    word-wrap: break-word !important;
                    white-space: initial !important;
                    word-break: keep-all;

                    span.is-leaf.el-tree-node__expand-icon.el-icon-caret-right::before {
                        content: "" !important;
                    }
                }
            }
        }

        .el-tree-node__content:hover, .el-tree-node:focus>.el-tree-node__content{
            background: transparent;
            background-color: transparent !important;
        }

    }

    .sidebar {
        // FEATURE IMAGE
        .image-container {
            margin-bottom: 24px;

            img {
                width: 100%;
            }
        }

        // CLEANING PLAN
        .cleaning-plan {
            position: sticky;
            top: 10px;
        }

        // PROMO CODE INPUT
        .promo-code {
            margin-bottom: 8px;

            .el-input {
                margin-bottom: 8px;
            }
            .el-button {
                width: 100%;
            }
        }

        // CLEANING PLAN DETAILS
        .date-hour {
            font-size: 14px;
            font-weight: 500;
            color: #6E918C;
            padding-bottom: 5px;
        }
        .category {
            font-size: 14px;
        }
        .service {
            font-size: 14px;
            padding-bottom: 16px;
            border-bottom: 1px solid #E5E9EF;
        }
        .summary {
            margin-top: 16px;
            padding-bottom: 8px;

            .pricing-item {
                font-size: 14px;
                display: flex;
                justify-content: space-between;
                font-weight: 500;
                margin-bottom: 8px;

                .label {
                    color: #545C69;
                    font-weight: 400;
                }

                .quantity-extra {
                    color: var(--color-primary);
                    font-size: 12px;
                }

                &.selected-giftcard {
                    justify-content: start;
                }
            }
            .selected-extras, .selected-giftcard {
                margin-top: -5px;

                .label {
                    color: var(--color-primary);
                    font-weight: 500;
                    font-size: 12px;
                }
            }
            .discount-rate {
                background-color: var(--color-primary);
                color: white;
                margin-left: 5px;
                font-size: 10px;
                font-weight: 600;
                padding: 2px 4px;
                border-radius: 4px;

            }
            .selected-discount {
                font-size: 12px;
                font-weight: 500;
                color: var(--color-primary)
            }
            .center-discount {
                display: flex;
                align-items: center;
            }
            .remove-discount {
                cursor: pointer;
                margin-left: 8px;

                img {
                    height: 13px;
                }
            }
        }
        .referrals {
            margin-top: 16px;

            .pricing-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;

                .balance-label {
                    color: #545C69;
                    font-size: 14px;
                }
                .balance-total {
                    font-size: 14px;
                    font-weight: 500;
                }
                .el-input-number--small {
                    border: none;
                    padding: 4px;
                    margin-right: -4px;
                }
            }

        }
        .total {
            font-size: 20px;
            font-weight: 600;
            display: flex;
            justify-content: space-between;
            padding-top: 16px;
            border-top: 1px solid #E5E9EF;

            .total-amount {
                color: var(--color-primary);
            }
        }

    }

    // RESPONSIVE DESIGN
    @media (max-width: 768px) {
        .sidebar:not(.responsive), .column-divider {
            display: none !important;
        }
        .card {
            padding: 30px 24px;
        }
        .card-brands {
            flex-direction: column-reverse;
            gap: 8px;
        }
    }

    @media (min-width: 768px) {
        .sidebar.responsive {
            display: none;
        }
    }

    // CUSTOM FIELD RADIO GROUP
    .custom-field-radio-group::v-deep {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 16px;

        @media (max-width: 768px) {
            grid-template-columns: 1fr !important;
        }

        .custom-field-radio {
            width: 100%;
            border: 1px solid #E5E9EF;
            border-radius: var(--input-border-radius);
            padding: 8px 16px;
            height: 44px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            font-size: 14px;
            width: 100%;

            .el-radio__label {
                color: #8492A6;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                line-height: 1.4;
                font-weight: 400;
            }

            .el-radio__inner {
                display: flex;
                justify-content: center;
                align-items: center;
                min-width: 16px;
                width: 16px;
                height: 16px;
                border-radius: 100px;
                border: 1px solid #E5E9EF;
                background-color: white;
                width: 18px;
                height: 18px;

                &::after {
                    background-color: var(--color-primary);
                    width: 10px;
                    height: 10px;
                }
            }

            &.is-checked {
                border-color: var(--color-primary);

                .el-radio__inner {
                    border-color: var(--color-primary);
                }
            }
        }
    }

    .error-alert::v-deep {
        border-radius: var(--input-border-radius);
        padding: 12px 16px;
        margin-bottom: 24px !important;

        .el-alert__content {
            padding: 0 12px;

            .el-alert__title {
                font-size: 14px;
            }
            .el-alert__description {
                font-size: 12px;
                margin-top: 2px;
            }
        }

        .el-alert__icon {
            width: 36px;
            font-size: 36px;
        }
    }

</style>
